import {useCallback, useMemo} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {CosSelectionOptionsService} from '../api/generated/CosSelectionOptionsService';
import {BasicPage} from '../components/basic-page';
import {BreadcrumbPart} from '../components/page-title.tsx';
import {ROUTES} from '../routes';
import {useNotification} from '../hooks/use-notifications.tsx';
import {SubmitHandler} from 'react-hook-form';
import {cosSelectionOptionSchema, CosSelectionOptionSchema} from './schemas.ts';
import {createForm} from '../forms';
import {Button, Group, Stack} from '@mantine/core';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {useAsync} from 'react-use';
import {AsyncStateContainer} from '../components/async-state-container.tsx';
import {css} from '@emotion/react';
import {getDefaults} from '../utils/zod.ts';

const breadcrumbs: BreadcrumbPart[] = [
  {title: 'COS Selection Options', href: ROUTES.ADMIN.COS_SELECTION_OPTIONS.INDEX.path},
  {title: 'Create'},
];

const Form = createForm<CosSelectionOptionSchema>();

export const CosSelectionOptionCreate = () => {
  const navigate = useNavigate();
  const notifications = useNotification();

  const onSubmit = useCallback<SubmitHandler<CosSelectionOptionSchema>>(
    async (value) => {
      const response = await CosSelectionOptionsService.create({body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('COS Option Created');
      navigate(ROUTES.ADMIN.COS_SELECTION_OPTIONS.INDEX.path);
    },
    [navigate, notifications],
  );

  return (
    <BasicPage title={breadcrumbs}>
      <Form
        defaultValues={getDefaults(cosSelectionOptionSchema)}
        schema={cosSelectionOptionSchema}
        onSubmit={onSubmit}
      >
        <Form.Container>
          <FormFields />
          <Group mt={24} justify={'flex-end'}>
            <Button
              type='button'
              variant='outline'
              component={Link}
              to={ROUTES.ADMIN.COS_SELECTION_OPTIONS.INDEX.path}
            >
              Cancel
            </Button>
            <Form.Submit>Create COS Option</Form.Submit>
          </Group>
        </Form.Container>
      </Form>
    </BasicPage>
  );
};

export const CosSelectionOptionUpdate = () => {
  const navigate = useNavigate();
  const notifications = useNotification();
  const {id} = useTypedParams(ROUTES.ADMIN.COS_SELECTION_OPTIONS.DETAIL);

  const fetchOption = useAsync(async () => {
    const {data} = await CosSelectionOptionsService.getById({id});
    return data;
  }, [id]);

  const option = fetchOption.value || undefined;
  const optionName = option?.label ?? '';

  const breadcrumbs = useMemo<BreadcrumbPart[]>(
    () => [
      {title: 'COS Selection Options', href: ROUTES.ADMIN.COS_SELECTION_OPTIONS.INDEX.path},
      {title: optionName},
    ],
    [optionName],
  );

  const onSubmit = useCallback<SubmitHandler<CosSelectionOptionSchema>>(
    async (value) => {
      const response = await CosSelectionOptionsService.update({id, body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('COS Option Updated');
      navigate(ROUTES.ADMIN.COS_SELECTION_OPTIONS.INDEX.path);
    },
    [id, navigate, notifications],
  );

  return (
    <BasicPage title={breadcrumbs}>
      <AsyncStateContainer {...fetchOption}>
        <Form
          schema={cosSelectionOptionSchema}
          onSubmit={onSubmit}
          defaultValues={option}
        >
          <Form.Container>
            <FormFields />
            <Group mt={24} justify='flex-end'>
              <Button
                type='button'
                variant='outline'
                component={Link}
                to={ROUTES.ADMIN.COS_SELECTION_OPTIONS.INDEX.path}
              >
                Cancel
              </Button>
              <Form.Submit>Update COS Option</Form.Submit>
            </Group>
          </Form.Container>
        </Form>
      </AsyncStateContainer>
    </BasicPage>
  );
};

const FormFields = () => {
  return (
    <>
      <Form.Row css={unlimitedChildWidth}>
        <Form.TextInput
          name='label'
          label='Label'
          placeholder='e.g. 360 days'
        />
        <Stack w='15%' css={withoutFlexGrow}>
          <Form.NumberInput name='days' label='Days' />
        </Stack>
        <Stack w='10%' css={withoutFlexGrow}>
          <Form.NumberInput name='sortOrder' label='Sort Order' />
        </Stack>
      </Form.Row>
      <Form.Row mt={12} ml={12} w='66%'>
        <Form.Checkbox name='isActive' label='Active' />
      </Form.Row>
    </>
  );
};

const withoutFlexGrow = css({
  flexGrow: 0,
});

const unlimitedChildWidth = css({
  '& > *': {
    maxWidth: '100%',
  },
});
